"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import type { BlogPost } from "~/types/blog";
import { useTranslations } from "next-intl";

import Image from "next/image";

export function BlogPreview({
  posts,
  locale,
}: {
  posts: BlogPost[];
  locale: string;
}) {
  const t = useTranslations("landing.blogPreview");

  return (
    <section className="bg-white py-24 dark:bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="mb-12 flex flex-col items-end justify-between gap-4 md:flex-row">
          <div className="max-w-2xl">
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
              {t("title")}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {t("description")}
            </p>
          </div>
          <Link href={`/${locale}/blog`}>
            <Button className="group rounded-full border-0 bg-blue-600 text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-700 hover:opacity-100">
              {t("viewAll")}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {posts.map((post) => (
            <Link key={post.slug} href={`/${locale}/blog/${post.slug}`}>
              <Card className="group h-full cursor-pointer overflow-hidden border-gray-200 bg-gray-50 transition-all hover:shadow-lg dark:border-gray-800 dark:bg-gray-900/50">
                <CardHeader className="p-0">
                  <div className="relative h-48 w-full overflow-hidden rounded-t-xl">
                    {post.coverImage ? (
                      <Image
                        src={post.coverImage}
                        alt={post.title}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-blue-600 to-teal-400" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <Badge
                      variant="secondary"
                      className="bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                    >
                      {post.category}
                    </Badge>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {post.readTime} min
                    </span>
                  </div>
                  <h3 className="mb-2 line-clamp-2 text-xl font-bold text-gray-900 transition-colors group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                    {post.title}
                  </h3>
                  <p className="line-clamp-3 text-sm text-gray-600 dark:text-gray-400">
                    {post.excerpt}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
